import {
  Datagrid,
  DataProviderContext,
  EditButton,
  FunctionField,
  List,
  ListActions,
  NumberField,
  ReferenceInput,
  SelectField,
  SimpleList,
  TextField,
  TextInput,
  useDataProvider,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import { checkPermission } from "server/security/utils";
import permissions from "app/permissions";
import { getPermissions } from "auth/security/securityUtils";
import SecuredComponent from "server/security/SecuredComponent";
import { BUILDINGS, DormitoryBuildingStatuses, DormitoryServicePath, MOBILE_BREAKPOINT } from "utils/constants";
import { useMemo } from "react";
import { compareBuildingNumbers } from "../Partials/compareBuildingNumbers";
import { DormitoryBuildingStatusChoices } from "utils/choices";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";
import { employeeRequests } from "api/employeeApi";
import { dormitoryRequests } from "api/dormitoryApi";
import EmployeeField from "server/components/Fields/EmployeeField";
import StatusToggleDialog from "../Partials/StatusToggleDialog";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import DatagridActions from "../Partials/DatagridActions";
import DatagridHeader from "../Partials/DatagridHeader";

const listFilters = [
  <TextInput
    source="residentialNumber"
    label="Номер"
    alwaysOn
  />,
  <ReferenceInput
    reference={employeeRequests.getEmployeesSelect()}
    label="Управител"
    source="houseManagerEmployeeId"
    alwaysOn
  >
    <DormitoryAutocompleteInput
      style={{ width: 280 }}
      label="Управител"
      optionText={rec =>
        rec.secondName ? `${rec.firstName} ${rec.secondName} ${rec.lastName}` : `${rec.firstName} ${rec.lastName}`
      }
    />
  </ReferenceInput>,
  <DormitoryAutocompleteInput
    source="status"
    label="Статус"
    choices={DormitoryBuildingStatusChoices}
    alwaysOn
    emptyText={"Всички"}
  />,
];

const buildingStatusToggle = record => (
  <SecuredComponent permission={permissions.canManageDormitoryBuildings}>
    <StatusToggleDialog
      isActive={record?.status === DormitoryBuildingStatuses.Active}
      activateUrl={dormitoryRequests.activateBuilding(record?.id)}
      deactivateUrl={dormitoryRequests.deactivateBuilding(record?.id)}
      confirmMessage="Сигурни ли сте, че искате да смените статуса на блока?"
      successMessage="Успешно сменихте статуса на блока"
    />
  </SecuredComponent>
);

const buildingActions = record => (
  <>
    <SecuredComponent permission={permissions.canManageDormitoryBuildings}>
      <EditButton label="Редактирай" />
    </SecuredComponent>
    {buildingStatusToggle(record)}
  </>
);

const renderOccupancy = record => {
  const totalBeds = record?.totalBeds ?? 0;
  const occupiedBeds = Math.max(0, totalBeds - (record?.freeBeds ?? 0));
  const percentage = totalBeds > 0 ? Math.round((occupiedBeds / totalBeds) * 100) : 0;

  return `${occupiedBeds} / ${totalBeds} (${percentage}%)`;
};

const createSortedBuildingsProvider = dataProvider => ({
  ...dataProvider,
  getList: async (resource, params) => {
    if (resource !== `${DormitoryServicePath}${BUILDINGS}`) {
      return dataProvider.getList(resource, params);
    }

    const buildings = [];
    let page = 1;
    let total;
    do {
      const result = await dataProvider.getList(resource, {
        ...params,
        pagination: { page, perPage: 500 },
      });
      total = result.total;
      if (result.data.length === 0 && buildings.length < total) {
        throw new Error("Списъкът с блокове не можа да се зареди. Опитайте отново.");
      }
      buildings.push(...result.data);
      page += 1;
    } while (buildings.length < total);

    buildings.sort(compareBuildingNumbers);
    const { page: currentPage, perPage } = params.pagination;
    return {
      data: buildings.slice((currentPage - 1) * perPage, currentPage * perPage),
      total,
    };
  },
});

const ListBuildings = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const dataProvider = useDataProvider();
  const sortedBuildingsProvider = useMemo(() => createSortedBuildingsProvider(dataProvider), [dataProvider]);

  return (
    <DataProviderContext.Provider value={sortedBuildingsProvider}>
      <List
        title="Блокове"
        sort={{ field: "residentialNumber", order: "ASC" }}
        exporter={false}
        filters={listFilters}
        filterDefaultValues={{ status: DormitoryBuildingStatuses.Active }}
        actions={<ListActions hasCreate={checkPermission(getPermissions(), permissions.canManageDormitoryBuildings)} />}
      >
        {isMobile ? (
          <SimpleList
            linkType={false}
            sx={settings.style.mobileList}
            primaryText={record => (
              <MobileListRow fields={[{ label: "Номер", value: record?.residentialNumber }]} />
            )}
            secondaryText={record => (
              <div>
                <MobileListRow fields={[{ label: "Управител", value: <EmployeeField source="houseManagerEmployeeId" /> }]} />
                <MobileListRow fields={[{ label: "Свободни легла", value: <TextField source="freeBeds" /> }]} />
                <MobileListRow fields={[{ label: "Заетост", value: renderOccupancy(record) }]} />
                <MobileListRow
                  fields={[
                    { label: "Статус", value: <SelectField source="status" choices={DormitoryBuildingStatusChoices} /> },
                  ]}
                />
              </div>
            )}
            tertiaryText={record => buildingActions(record, true)}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
          >
            <TextField
              source="residentialNumber"
              label={<DatagridHeader align="left">Номер</DatagridHeader>}
              sortable={false}
            />
            <EmployeeField
              source="houseManagerEmployeeId"
              label={<DatagridHeader align="left">Управител</DatagridHeader>}
              sortable={false}
            />
            <NumberField
              source="freeBeds"
              label={<DatagridHeader align="right">Свободни легла</DatagridHeader>}
              textAlign="right"
              sortable={false}
            />
            <FunctionField
              source="occupancy"
              label={<DatagridHeader align="right">Заетост</DatagridHeader>}
              render={renderOccupancy}
              textAlign="right"
              sortable={false}
            />
            <SelectField
              source="status"
              label={<DatagridHeader align="left">Статус</DatagridHeader>}
              choices={DormitoryBuildingStatusChoices}
              sortable={false}
            />
            <FunctionField
              source="actions"
              label={false}
              sortable={false}
              render={record => <DatagridActions>{buildingActions(record)}</DatagridActions>}
            />
          </Datagrid>
        )}
      </List>
    </DataProviderContext.Provider>
  );
};
export default ListBuildings;
